/**
 * Regras puras da visão do participante na Realização Destaque: o semestre (ciclo) inteiro numa leitura,
 * sem seletor de fase. Datas em milissegundos.
 */
import { textoDaContagem } from 'utils/strings';

import { nivelAguardado } from './pilula-da-realizacao';

/** Item de `find/entregas` usado pela página (subconjunto). */
export interface Entrega {
  _id: string;
  owner: string;
  owner_name_guerra?: string;
  title: string;
  /** aguardando | rejeitado | aprovado | dobrado | promovido | selo | concluido | arquivado | ... */
  status: string;
  created: number;
  updated?: number;
  stageNumber?: number;
  multiplicador?: number;
  team?: { _id: string; percent?: number; description?: string }[];
  now?: number;
  indicator?: { cod?: number | string; nome?: string };
}

export interface FaseDoCiclo {
  _id: string;
  title: string;
  type?: string;
  status: string;
  date_start: number;
  date_finish: number;
  /** Janelas de apreciação da fase (`calendar`); usadas pela regra de edição. */
  date_evaluation_step2_start?: number;
  date_evaluation_step2_finish?: number;
  date_evaluation_step3_start?: number;
  date_evaluation_step3_finish?: number;
  date_evaluation_step4_start?: number;
  date_evaluation_step4_finish?: number;
  date_evaluation_pitStop_start?: number;
  date_evaluation_pitStop_finish?: number;
  date_evaluation_step5_start?: number;
  date_evaluation_step5_finish?: number;
}

/** Ciclo de `cyclesExtended` (subconjunto). */
export interface CicloDoSemestre {
  _id: string;
  title: string;
  status: string;
  date_start: number;
  date_end: number;
  steps?: FaseDoCiclo[];
}

export const FASE_NAO_IDENTIFICADA = 'Fase não identificada';

/** Mesmos padrões dos contadores da página atual (`MissaoDestaque/index.tsx`). */
const STATUS_VALIDADA = /(aprovado|dobrado|promovido|selo|concluido)/gi;
const STATUS_AGUARDANDO = /(aguardando)/gi;

/** RDN-01: janela de `find/entregas` = o ciclo inteiro. */
export function janelaDoCiclo(ciclo: Pick<CicloDoSemestre, 'date_start' | 'date_end'>): { start: number; end: number } {
  return { start: ciclo.date_start, end: ciclo.date_end };
}

function fasesMensais(fases: FaseDoCiclo[] | undefined): FaseDoCiclo[] {
  return (fases ?? []).filter(fase => fase.type === 'month');
}

function contem(fase: FaseDoCiclo, instante: number): boolean {
  return instante >= fase.date_start && instante <= fase.date_finish;
}

/**
 * Ciclos antigos (1S2023 a 1S2024) só têm fases mensais e elas vêm sem `type`; na página antiga o seletor passava
 * essa fase ao `TaskItem` do mesmo jeito. Nos ciclos novos, semestre e trimestre têm `type` e ficam de fora.
 */
export function ehFaseMensalDaRealizacao(fase: Pick<FaseDoCiclo, 'type'>): boolean {
  return fase.type === 'month' || !fase.type;
}

/** Fases mensais do ciclo; nos ciclos antigos, sem `type`, todas as fases. */
export function fasesMensaisDoCiclo<F extends FaseDoCiclo>(fases: F[] | undefined): F[] {
  return (fases ?? []).filter(ehFaseMensalDaRealizacao);
}

/** Fase mensal que contém o instante (cadastro da realização, crédito de pontos), se houver. */
export function faseMensalDe<F extends FaseDoCiclo>(instante: number, fases: F[] | undefined): F | undefined {
  return fasesMensaisDoCiclo(fases).find(fase => contem(fase, instante));
}

/** RDN-06: título da fase mensal em que a realização foi cadastrada. */
export function faseDaRealizacao(created: number, fases: FaseDoCiclo[] | undefined): string {
  return faseMensalDe(created, fases)?.title ?? FASE_NAO_IDENTIFICADA;
}

/** Item com data de cadastro: realização de `find/entregas` ou excluída de `find/entregas_excluidas`. */
interface Cadastrada {
  created: number;
}

const maisRecentePrimeiro = (a: Cadastrada, b: Cadastrada) => b.created - a.created;

/** RDN-04 e RDN-05: realizações próprias e participações, sem as arquivadas, da mais recente para a mais antiga. */
export function separarRealizacoes(tarefas: Entrega[], chave: string): { minhas: Entrega[]; participacoes: Entrega[] } {
  const ativas = tarefas.filter(tarefa => tarefa.status !== 'arquivado');

  return {
    minhas: ativas.filter(tarefa => tarefa.owner === chave).sort(maisRecentePrimeiro),
    participacoes: ativas.filter(tarefa => tarefa.owner !== chave).sort(maisRecentePrimeiro),
  };
}

/** RDV-04: as três seções da lista do ciclo, cada uma da mais recente para a mais antiga. */
export function secoesDoCiclo<E extends Cadastrada>(
  tarefas: Entrega[],
  excluidas: E[],
  chave: string,
): { minhas: Entrega[]; participacoes: Entrega[]; excluidas: E[] } {
  return { ...separarRealizacoes(tarefas, chave), excluidas: [...excluidas].sort(maisRecentePrimeiro) };
}

/** Filtro de período da lista (RDV-07). */
export type Periodo = 'ciclo' | 'mes';

const porInicio = (a: FaseDoCiclo, b: FaseDoCiclo) => a.date_start - b.date_start;

function mesesIniciados<F extends FaseDoCiclo>(fases: F[] | undefined, agora: number): F[] {
  return fasesMensaisDoCiclo(fases)
    .filter(fase => fase.date_start <= agora)
    .sort(porInicio);
}

/** RDV-11: a realização foi cadastrada na fase mensal do chip. */
export function noMes(created: number, faseId: string, fases: FaseDoCiclo[] | undefined): boolean {
  return faseMensalDe(created, fases)?._id === faseId;
}

/** RDV-09/RDV-12: um chip por fase mensal já iniciada, com os cadastros dela; fora de toda fase não conta. */
export function mesesDoCiclo<F extends FaseDoCiclo>(
  fases: F[] | undefined,
  itens: Cadastrada[],
  agora: number,
): { fase: F; quantidade: number }[] {
  return mesesIniciados(fases, agora).map(fase => ({
    fase,
    quantidade: itens.filter(item => noMes(item.created, fase._id, fases)).length,
  }));
}

/** RDV-10: o chip marcado ao escolher "Mês": a fase mensal aberta e, sem ela, a última já iniciada. */
export function mesPadrao<F extends FaseDoCiclo>(fases: F[] | undefined, agora: number): F | undefined {
  const iniciados = mesesIniciados(fases, agora);
  return iniciados.find(fase => fase.status === 'open') ?? iniciados[iniciados.length - 1];
}

/** RDV-08/RDV-09: a dica abaixo do filtro de período. */
export function dicaDoPeriodo(periodo: Periodo, ciclo: Pick<CicloDoSemestre, 'title' | 'steps'>): string {
  if (periodo === 'mes') return 'Cada mês de cadastramento é apreciado no Nível 1.';

  const meses = fasesMensaisDoCiclo(ciclo.steps).sort(porInicio);
  // Ciclo sem fase mensal (não ocorre nos dados): só o nome do ciclo, sem intervalo.
  if (!meses.length) return `Todas as realizações do ciclo ${ciclo.title}.`;

  return `Todas as realizações do ciclo ${ciclo.title}, de ${meses[0].title} a ${meses[meses.length - 1].title}.`;
}

/**
 * RDV-13/RDV-15: pílulas do topo, contadas no ciclo inteiro. `pilulaPorId` é a pílula que cada cartão calculou
 * (RDV-14); "aguardando validação" e um "aguardando o Nível X" por nível só aparecem com contagem.
 */
export function pilulasDoCiclo(minhas: Entrega[], participacoes: Entrega[], pilulaPorId: Record<string, string>): string[] {
  const pilulas = [...minhas, ...participacoes].map(realizacao => pilulaPorId[realizacao._id] ?? '');
  const aguardandoValidacao = pilulas.filter(pilula => pilula === 'Aguardando validação').length;
  const porNivel = new Map<number, number>();
  pilulas.forEach(pilula => {
    const nivel = nivelAguardado(pilula);
    if (nivel) porNivel.set(nivel, (porNivel.get(nivel) ?? 0) + 1);
  });

  return [
    textoDaContagem(minhas.length, 'cadastrada por você', 'cadastradas por você'),
    textoDaContagem(participacoes.length, 'participação', 'participações'),
    ...(aguardandoValidacao ? [`${aguardandoValidacao} aguardando validação`] : []),
    ...Array.from(porNivel)
      .sort(([a], [b]) => a - b)
      .map(([nivel, quantidade]) => `${quantidade} aguardando o Nível ${nivel}`),
  ];
}

export interface ContadoresDoSemestre {
  cadastradas: number;
  validadas: number;
  aguardando: number;
  participacoes: number;
}

// SPEC_DEVIATION: T15 pede que `contadoresDoSemestre` saia sem referência restante.
// Reason: o resumo do Extrato (`pages/Extrato/index.tsx`) usa a função; só o componente da página saiu (RDV-02).
/** RDN-07: os quatro contadores do ciclo, hoje só para o resumo do Extrato. */
export function contadoresDoSemestre(tarefas: Entrega[], chave: string): ContadoresDoSemestre {
  const { minhas, participacoes } = separarRealizacoes(tarefas, chave);

  return {
    cadastradas: minhas.length,
    validadas: tarefas.filter(tarefa => !!tarefa.status?.match(STATUS_VALIDADA)).length,
    aguardando: tarefas.filter(tarefa => !!tarefa.status?.match(STATUS_AGUARDANDO)).length,
    participacoes: participacoes.length,
  };
}

export type EstadoDoCadastro =
  /** RDN-22/23: fase aberta, ainda sem realização própria nela. */
  | { tipo: 'aberto-sem-realizacao'; fase: FaseDoCiclo }
  /** RDN-22/24: fase aberta, já com realização própria; `editavel` decide entre "Editar" e "Você já cadastrou". */
  | { tipo: 'aberto-com-realizacao'; fase: FaseDoCiclo; realizacaoId: string; editavel: boolean }
  /** RDN-27: o cadastro está aberto em outro ciclo (o aberto), não no selecionado. */
  | { tipo: 'em-outro-ciclo'; ciclo: Pick<CicloDoSemestre, '_id' | 'title'>; fase: FaseDoCiclo }
  /** RDN-26: nenhuma fase mensal aberta; `proximaAbertura` é o início do próximo cadastramento, se houver. */
  | { tipo: 'fechado'; proximaAbertura?: number }
  /** Fase aberta no ciclo selecionado, mas o usuário não pode cadastrar (RDN-22 não vale): o topo mostra o prazo, sem chamar. */
  | { tipo: 'indisponivel'; fase: FaseDoCiclo };

export interface EntradaDoEstadoDoCadastro {
  ciclos: CicloDoSemestre[];
  cicloSelecionadoId: string | undefined;
  /** Realizações próprias do ciclo selecionado (RDN-04). */
  minhas: Entrega[];
  agora: number;
  /** `user.extra.task !== false && !playerInfo.visualizador`, as condições de hoje. */
  podeCadastrar: boolean;
  /** Regra de edição de hoje (`taskEditable`). */
  editavel: (realizacao: Entrega) => boolean;
}

export function proximoCadastramento(ciclos: CicloDoSemestre[], agora: number): number | undefined {
  const inicios = ciclos
    .flatMap(ciclo => fasesMensais(ciclo.steps))
    .map(fase => fase.date_start)
    .filter(inicio => inicio > agora);

  return inicios.length ? Math.min(...inicios) : undefined;
}

/** RDN-22 a RDN-27: o que o topo do participante mostra sobre o cadastramento. */
export function estadoDoCadastro({
  ciclos,
  cicloSelecionadoId,
  minhas,
  agora,
  podeCadastrar,
  editavel,
}: EntradaDoEstadoDoCadastro): EstadoDoCadastro {
  const cicloAberto = ciclos.find(ciclo => ciclo.status === 'open');
  const faseAberta = fasesMensais(cicloAberto?.steps).find(fase => fase.status === 'open');

  if (!cicloAberto || !faseAberta) {
    return { tipo: 'fechado', proximaAbertura: proximoCadastramento(ciclos, agora) };
  }

  if (cicloSelecionadoId !== cicloAberto._id) {
    return { tipo: 'em-outro-ciclo', ciclo: { _id: cicloAberto._id, title: cicloAberto.title }, fase: faseAberta };
  }

  if (!podeCadastrar) return { tipo: 'indisponivel', fase: faseAberta };

  const realizacaoDaFase = minhas.find(realizacao => contem(faseAberta, realizacao.created));

  if (!realizacaoDaFase) return { tipo: 'aberto-sem-realizacao', fase: faseAberta };

  return {
    tipo: 'aberto-com-realizacao',
    fase: faseAberta,
    realizacaoId: realizacaoDaFase._id,
    editavel: editavel(realizacaoDaFase),
  };
}

/**
 * Etapa de apreciação em andamento na fase (`getActiveEvaluationStep` de `MissaoDestaque/index.tsx`, sem mudar a
 * regra): 2 validação, 3 Nível 1, 4 Nível 2, 4.1 Pit Stop, 5 Nível 3, 0 nenhuma; `null` sem fase ou sem horário.
 */
export function etapaDeApreciacaoAtiva(fase: FaseDoCiclo | undefined, agora: number): 0 | 2 | 3 | 4 | 4.1 | 5 | null {
  if (!fase || !agora) return null;

  const dentro = (inicio?: number, fim?: number) => agora > (inicio as number) && agora < (fim as number);

  if (dentro(fase.date_evaluation_step2_start, fase.date_evaluation_step2_finish)) return 2;
  if (dentro(fase.date_evaluation_step3_start, fase.date_evaluation_step3_finish)) return 3;
  if (dentro(fase.date_evaluation_step4_start, fase.date_evaluation_step4_finish)) return 4;
  if (dentro(fase.date_evaluation_pitStop_start, fase.date_evaluation_pitStop_finish)) return 4.1;
  if (dentro(fase.date_evaluation_step5_start, fase.date_evaluation_step5_finish)) return 5;
  return 0;
}

/**
 * RDN-10/RDN-24: regra de edição de hoje (`taskEditable` de `MissaoDestaque/index.tsx`). `fase` é a fase mensal em
 * que a realização foi cadastrada (na página antiga, a fase selecionada que a listava). A condição de aba
 * "cadastrada" de lá equivale ao dono = usuário: a outra aba só tinha realizações de outros.
 */
export function realizacaoEditavel(
  realizacao: Pick<Entrega, 'owner' | 'created' | 'status'>,
  fase: FaseDoCiclo | undefined,
  chave: string | undefined,
  agora: number,
): boolean {
  if (!fase || !chave) return false;

  if (realizacao.owner === chave && realizacao.created >= fase.date_start && realizacao.created <= fase.date_finish) {
    if (realizacao.status === 'aguardando' && fase.status === 'open') return true;

    const etapa = etapaDeApreciacaoAtiva(fase, agora);
    if (realizacao.status === 'rejeitado' && etapa && etapa === 2) return true;

    return false;
  }

  return false;
}

/** Início do calendário em que a troca de indicador passou a valer (05/04/2025), como na página antiga. */
const INICIO_DA_TROCA_DE_INDICADOR = 1743822000000;

/** RDN-10: regra de "Atualizar indicador" de hoje (`taskCanUpdateIndicator` de `MissaoDestaque/index.tsx`). */
export function podeAtualizarIndicador(
  realizacao: Pick<Entrega, 'owner' | 'indicator'>,
  fase: FaseDoCiclo | undefined,
  chave: string | undefined,
): boolean {
  if (!fase || !chave || fase.date_start < INICIO_DA_TROCA_DE_INDICADOR) return false;

  if (realizacao.owner === chave) {
    if (!realizacao.indicator) return true;
    if (realizacao.indicator.cod === 1 || realizacao.indicator.cod === '1') return true;
    return false;
  }

  return false;
}

/** Leitura de `find/info` usada para decidir o perfil (subconjunto). */
export interface PerfilDoJogador {
  cod_etapa?: number;
  cod_etapa_sub?: number;
  mad_dupla?: { cod_etapa?: number; cod_etapa_sub?: number }[];
  visualizador?: boolean;
}

const nivelDeGestor = (valor: unknown) => typeof valor === 'number' && valor > 0 && valor <= 5;

/** Mesma regra `isGestor` de `MissaoDestaque/index.tsx`: `cod_etapa`/`cod_etapa_sub` de 1 a 5, próprio ou de `mad_dupla`. */
export function ehGestor(perfil: PerfilDoJogador | null | undefined): boolean {
  const dupla = Array.isArray(perfil?.mad_dupla) ? perfil?.mad_dupla ?? [] : [];
  return (
    (!!perfil?.cod_etapa && perfil.cod_etapa > 0 && perfil.cod_etapa <= 5) ||
    nivelDeGestor(perfil?.cod_etapa_sub) ||
    dupla.some(par => nivelDeGestor(par.cod_etapa)) ||
    dupla.some(par => nivelDeGestor(par.cod_etapa_sub))
  );
}
